/**
 * Curator Bridge Slice-4 capability compatibility shim.
 *
 * `public.user_profiles.can_curate_signals` is created by the accepted 3W
 * migration, but the generated Database type predates it, so `UserProfile` -
 * which is `Tables<'user_profiles'>` - does not declare the field.
 *
 * Regenerating the types now would make this WORSE, not better: the environment
 * available for generation does not have the accepted migrations installed, so
 * generation would reproduce a schema with no can_curate_signals,
 * publication_state, publication_version, v_signal_curation_queue or
 * signal_publication_decisions - overwriting a stale file with a differently
 * stale one. Regeneration belongs to controlled activation, against an
 * environment where the migrations actually exist.
 *
 * Until then this file is the ONLY place that bridges the gap, and it fails
 * closed. The profile loader already selects the whole row (`select('*')`), so
 * the value reaches the client the moment the column exists; before that it is
 * simply absent, which reads as false.
 *
 * REMOVAL: once the types are regenerated against an environment carrying the
 * column, delete this file and replace each call site with
 * `profile.can_curate_signals === true`. Nothing else depends on it.
 */

import type { UserProfile } from '@/auth/types';

/**
 * The single optional field this shim reads. Typed `unknown` rather than
 * `boolean` on purpose: the point is that the runtime value is not yet
 * guaranteed by the schema type, so it must be proven at the boundary instead
 * of asserted.
 */
export interface CuratorCapableProfile {
  can_curate_signals?: unknown;
}

/**
 * True only for the boolean `true`.
 *
 * Strict equality, never truthiness. `undefined` (column absent before
 * activation), `null`, `false`, `0`, `1`, `"true"` and `"false"` all read as
 * false. A truthy check would grant curation on the STRING "false", which is
 * exactly the kind of value a stale or hand-edited payload can carry.
 *
 * This is a presentation gate only. It hides an affordance; it grants nothing.
 * `curate_signal` re-derives the capability server-side, and
 * `bdse_record_publication_decision` re-reads user_profiles again inside the
 * transaction that writes the decision, so a bypassed UI still decides nothing.
 */
export function isCuratorCapable(profile: UserProfile): boolean {
  const capable = (profile as CuratorCapableProfile).can_curate_signals;
  return capable === true;
}
